import { useContext, useRef, useState } from 'react'
import { CalendarDays } from 'lucide-react'
import { cn } from '@/lib/utils'
import { FieldContext } from './Field'

/** Picking a slot without the browser's own date box, which shows
 *  "mm/dd/yyyy" and "09:00 AM" whenever the browser runs in English. The date
 *  reads "10. 18. (일)" and opens the system calendar; the time is two plain
 *  lists, 24-hour. Values stay in the `datetime-local` form
 *  ("2026-10-18T21:00", the browser's zone) — turning that into UTC is
 *  `fromLocalInput`'s job alone, in DateTimeField and SlotForm. */

const WEEKDAY = ['일', '월', '화', '수', '목', '금', '토']
const HOURS = Array.from({ length: 24 }, (_, h) => String(h).padStart(2, '0'))
const STEP_MINUTES = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, '0'))
const DEFAULT_TIME = '09:00'

type Size = 'sm' | 'md'

const box = (size: Size) =>
  cn(
    'rounded-md border border-input bg-card text-foreground tabular-nums transition-colors hover:border-foreground/25',
    'focus-within:border-accent focus-within:ring-2 focus-within:ring-ring/30',
    size === 'sm' ? 'h-8 text-table' : 'h-9 text-body',
  )

/** Every five minutes, plus the slot's own minute when the pacing put it off
 *  the grid (21:07) — so opening a slot never changes it. */
function minuteOptions(current: string): string[] {
  if (!current || STEP_MINUTES.includes(current)) return STEP_MINUTES
  return [...STEP_MINUTES, current].sort()
}

function parseDay(v: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v)
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null
}

/** "10. 18. (일)", with the year when it is another year. */
export function dayText(d: Date, now = new Date()): string {
  const base = `${d.getMonth() + 1}. ${d.getDate()}. (${WEEKDAY[d.getDay()]})`
  return d.getFullYear() === now.getFullYear() ? base : `${d.getFullYear()}. ${base}`
}

function DatePart({
  value,
  onChange,
  label,
  size,
  disabled,
  id,
}: {
  value: string
  onChange: (day: string) => void
  label: string
  size: Size
  disabled?: boolean
  id?: string
}) {
  const ref = useRef<HTMLInputElement>(null)
  const d = parseDay(value)
  return (
    <span className={cn('relative inline-flex min-w-[8.5rem] items-center gap-1.5 px-2.5', box(size), disabled && 'opacity-50')}>
      <CalendarDays size={14} className="shrink-0 text-muted-foreground" aria-hidden />
      <span aria-hidden className={cn('whitespace-nowrap', !d && 'text-muted-foreground')}>
        {d ? dayText(d) : '날짜'}
      </span>
      {/* The real control, invisible over the label: a tap opens the phone's
          own date wheel, a click asks the desktop browser for its calendar. */}
      <input
        ref={ref}
        id={id}
        type="date"
        aria-label={`${label} 날짜`}
        value={value}
        disabled={disabled}
        onChange={(e) => e.target.value && onChange(e.target.value)}
        onClick={() => {
          try {
            ref.current?.showPicker?.()
          } catch {
            // older browsers: the tap on the input itself still opens it
          }
        }}
        className={cn(
          'absolute inset-0 h-full w-full cursor-pointer opacity-0 disabled:cursor-not-allowed',
          '[&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0',
          '[&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:w-full',
          '[&::-webkit-calendar-picker-indicator]:cursor-pointer',
        )}
      />
    </span>
  )
}

export interface TimePickerProps {
  /** "21:00", or '' for none yet. */
  value: string
  onChange: (hhmm: string) => void
  label: string
  size?: Size
  disabled?: boolean
  /** Inside a SlotPicker: the outer group already carries the name. */
  bare?: boolean
  className?: string
}

/** Hour and minute, 24-hour: "21 : 00". */
export function TimePicker({ value, onChange, label, size = 'md', disabled, bare, className }: TimePickerProps) {
  const m = /^(\d{2}):(\d{2})/.exec(value)
  const hour = m?.[1] ?? ''
  const minute = m?.[2] ?? ''
  const set = (h: string, mi: string) => onChange(`${h || DEFAULT_TIME.slice(0, 2)}:${mi || '00'}`)
  const select = 'h-full cursor-pointer appearance-none bg-transparent px-2 text-center focus:outline-none disabled:cursor-not-allowed'
  return (
    <span
      role={bare ? undefined : 'group'}
      aria-label={bare ? undefined : label}
      className={cn('inline-flex items-center', box(size), disabled && 'opacity-50', className)}
    >
      <select aria-label={`${label} 시`} value={hour} disabled={disabled} onChange={(e) => set(e.target.value, minute)} className={select}>
        {!hour && <option value="">--</option>}
        {HOURS.map((h) => (
          <option key={h} value={h}>
            {h}
          </option>
        ))}
      </select>
      <span aria-hidden className="text-muted-foreground">
        :
      </span>
      <select aria-label={`${label} 분`} value={minute} disabled={disabled} onChange={(e) => set(hour, e.target.value)} className={select}>
        {!minute && <option value="">--</option>}
        {minuteOptions(minute).map((mi) => (
          <option key={mi} value={mi}>
            {mi}
          </option>
        ))}
      </select>
    </span>
  )
}

export interface SlotPickerProps {
  /** "2026-10-18T21:00" in the browser's zone, or '' for no slot yet. */
  value: string
  onChange: (local: string) => void
  /** Names the group; the parts read "<label> 날짜", "<label> 시", "<label> 분". */
  label: string
  size?: Size
  disabled?: boolean
  className?: string
}

/** A date and a time. Choosing a date first fills the time with 09:00, which
 *  shows. Choosing a time first does not invent a date: the hour waits, the
 *  picker says a date is still needed, and what it hands back stays empty
 *  until there is one — it used to fill in today without a word. */
export function SlotPicker({ value, onChange, label, size = 'md', disabled, className }: SlotPickerProps) {
  const field = useContext(FieldContext)
  const m = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/.exec(value)
  const day = m?.[1] ?? ''
  const time = m?.[2] ?? ''
  const [waiting, setWaiting] = useState('')
  const shownTime = time || waiting
  const emit = (d: string, t: string) => {
    if (!d) {
      setWaiting(t)
      onChange('')
      return
    }
    setWaiting('')
    onChange(`${d}T${t || DEFAULT_TIME}`)
  }
  return (
    <span className={cn('inline-flex flex-col gap-1', className)}>
      <span role="group" aria-label={label} className="inline-flex flex-wrap items-center gap-1.5">
        <DatePart value={day} onChange={(d) => emit(d, shownTime)} label={label} size={size} disabled={disabled} id={field?.id} />
        <TimePicker value={shownTime} onChange={(t) => emit(day, t)} label={label} size={size} disabled={disabled} bare />
      </span>
      {!day && waiting && (
        <span role="status" className="text-meta text-muted-foreground">
          날짜도 고른다
        </span>
      )}
    </span>
  )
}
